import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { interviewApi } from '@/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Icon,
  LinkButton,
  Modal,
  Textarea,
  useToast,
} from '@/components/ui';
import { BlockedAction } from '@/features/applications/CandidateRestriction';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

import { RescheduleRequestDialog } from './RescheduleRequestDialog';
import { RescheduleRequestPanel } from './RescheduleRequestPanel';
import { hasOpenRequest } from './reschedule';
import {
  INTERVIEW_LABELS,
  INTERVIEW_TONES,
  RESPONSE_LABELS,
  RESPONSE_TONES,
  interviewWhen,
} from './status';
import { studentInterviewActions } from './studentActions';

const JOIN_CALL_CLASS =
  'border border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 ' +
  'rounded-input inline-flex min-h-8 max-w-full items-center ' +
  'justify-center gap-2 px-3 py-1 text-sm font-medium transition';

/**
 * The interview rounds of one application, on the student's application
 * page, with the same actions as the Interviews list
 * (`studentInterviewActions`): answer once, join an accepted call, ask for
 * another time or withdraw that request.
 */
export function StudentApplicationRounds({ applicationId }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [requesting, setRequesting] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [message, setMessage] = useState('');
  const params = { application_id: applicationId, page_size: 20 };
  const { data } = useQuery({
    queryKey: ['student-interviews', params],
    queryFn: () => interviewApi.listInterviews(params),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['student-interviews'] });
    void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
  }

  const respond = useMutation({
    mutationFn: ({ id, response, message: note }) =>
      interviewApi.respondToInterview(id, response, note || null),
    onSuccess: (interview) => {
      toast.success(
        interview.student_response === 'ACCEPTED'
          ? 'Confirmed. Good luck!'
          : 'The company has been told you cannot make it.',
      );
      refresh();
      setDeclining(null);
      setMessage('');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not send your answer')),
  });

  const withdraw = useMutation({
    mutationFn: (id) => interviewApi.withdrawRescheduleRequest(id),
    onSuccess: () => {
      toast.success('Request withdrawn. Your interview stays at its current time.');
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not withdraw the request')),
  });

  // Latest round first (5, 4, 3…), by round number rather than date.
  const rounds = [...(data?.data ?? [])].sort((a, b) => b.round_number - a.round_number);
  if (rounds.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Interviews"
        action={
          <LinkButton to={paths.student.interviews} variant="ghostBrand" size="sm">
            All interviews
          </LinkButton>
        }
      />
      <ol className="space-y-3">
        {rounds.map((interview) => {
          const open = hasOpenRequest(interview);
          const can = studentInterviewActions(interview);
          return (
            <li key={interview.id} className="border-line rounded-input border p-3">
              <p className="text-ink text-sm font-semibold">
                Round {interview.round_number} · {interview.title}
              </p>
              <p className="text-ink-muted mt-0.5 text-xs">{interviewWhen(interview)}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge dot tone={INTERVIEW_TONES[interview.status] ?? 'neutral'}>
                  {INTERVIEW_LABELS[interview.status] ?? interview.status}
                </Badge>
                {interview.status === 'SCHEDULED' && !open && (
                  <Badge tone={RESPONSE_TONES[interview.student_response] ?? 'neutral'}>
                    {RESPONSE_LABELS[interview.student_response]}
                  </Badge>
                )}
              </div>
              <RescheduleRequestPanel request={interview.reschedule_request} audience="student" />
              {(interview.can_respond || can.lapsed) && (
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  {can.withdraw.show && (
                    <Button
                      variant="ghostDanger"
                      size="sm"
                      isLoading={withdraw.isPending && withdraw.variables === interview.id}
                      onClick={() => withdraw.mutate(interview.id)}
                    >
                      Withdraw request
                    </Button>
                  )}
                  {can.request.show && (
                    <BlockedAction reason={can.request.disabled ? can.request.title : null}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={can.request.disabled}
                        onClick={() => setRequesting(interview)}
                      >
                        <Icon name="repeat" size={14} />
                        Request another time
                      </Button>
                    </BlockedAction>
                  )}
                  {can.decline.show && (
                    <BlockedAction reason={can.decline.disabled ? can.decline.title : null}>
                      <Button
                        variant="tintDanger"
                        size="sm"
                        disabled={can.decline.disabled}
                        onClick={() => setDeclining(interview)}
                      >
                        <Icon name="close" size={14} />
                        {can.decline.label}
                      </Button>
                    </BlockedAction>
                  )}
                  {can.accept.show && (
                    <BlockedAction reason={can.accept.disabled ? can.accept.title : null}>
                      <Button
                        variant="tintPositive"
                        size="sm"
                        disabled={can.accept.disabled}
                        isLoading={respond.isPending && respond.variables?.id === interview.id}
                        onClick={() => respond.mutate({ id: interview.id, response: 'ACCEPTED' })}
                      >
                        <Icon name="check" size={14} />
                        {can.accept.label}
                      </Button>
                    </BlockedAction>
                  )}
                  {can.join.show &&
                    (can.join.disabled ? (
                      <BlockedAction reason={can.join.title}>
                        <Button variant="tintBrand" size="sm" disabled>
                          <Icon name="external" size={14} />
                          Join the call
                        </Button>
                      </BlockedAction>
                    ) : (
                      <a
                        href={can.join.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={JOIN_CALL_CLASS}
                      >
                        <Icon name="external" size={14} />
                        Join the call
                      </a>
                    ))}
                </div>
              )}
              {interview.can_respond &&
                can.request.show &&
                can.request.disabled &&
                can.request.title && (
                  <p className="text-ink-muted mt-1 text-right text-xs">{can.request.title}</p>
                )}
            </li>
          );
        })}
      </ol>
      {requesting && (
        <RescheduleRequestDialog interview={requesting} onClose={() => setRequesting(null)} />
      )}
      <Modal
        open={Boolean(declining)}
        title="Decline the invitation"
        description="The company sees your message. Need a different time instead? Use “Request another time”."
        onClose={() => setDeclining(null)}
      >
        <div className="space-y-4">
          <Textarea
            label="Message"
            rows={3}
            placeholder="I have an exam that morning."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeclining(null)}>
              Back
            </Button>
            <Button
              variant="danger"
              isLoading={respond.isPending}
              onClick={() =>
                respond.mutate({ id: declining.id, response: 'DECLINED', message: message.trim() })
              }
            >
              Decline
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
